import React, { useState, useEffect, useRef } from 'react';
import { RewriteResumeResponse } from '@/api/types';
import { ATSResumeJson } from '@/types/resume';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import { ResumeToolbar } from './ResumeToolbar';
import { ResumeFeedbackPanel } from './ResumeFeedbackPanel';
import { ResumeContent } from './ResumeContent';
import { useTailoredResume } from './useTailoredResume';
import { pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface TailoredResumePanelProps {
  result: RewriteResumeResponse;
  onSave: () => void;
  isSaving: boolean;
  resume?: any;
  jobText?: string;
  recommendations?: any[];
  onUpdateResult?: (res: RewriteResumeResponse) => void;
  onRetry?: () => void;
}

export function TailoredResumePanel(props: TailoredResumePanelProps) {
  const {
    isDownloading,
    resumeData,
    history,
    historyIndex,
    pdfBlob,
    numPages,
    setNumPages,
    isGenerating,
    showChanges,
    setShowChanges,
    zoom,
    setZoom,
    feedback,
    setFeedback,
    error,
    isEditingJson,
    editingData,
    setEditingData,
    handleGenerate,
    handleCancelGenerate,
    handleUndo,
    handleRedo,
    handleDownloadPDF,
    handleToggleEditMode,
    handleSaveToExtension,
    setIsEditingJson
  } = useTailoredResume(props);

  const rootRef = useRef<HTMLDivElement>(null);
  const [isWide, setIsWide] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(600);
  
  useEffect(() => {
    if (!rootRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setIsWide(entry.contentRect.width >= 920);
      }
    });
    observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setContainerWidth(Math.min(entry.contentRect.width - 48, 800));
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY || e.detail || (e as any).wheelDelta;
        if (delta > 0) {
          setZoom(z => Math.max(0.5, z - 0.1));
        } else if (delta < 0) {
          setZoom(z => Math.min(2, z + 0.1));
        }
      }
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', handleWheel);
    };
  }, [pdfBlob, isGenerating, setZoom]);

  const json = resumeData;

  return (
    <div ref={rootRef} className="flex flex-col flex-1 bg-slate-50 dark:bg-slate-900 relative rounded-xl border border-slate-200 dark:border-slate-800 h-full overflow-hidden dark:shadow-sm min-h-0">
      <ResumeToolbar
        isWide={isWide}
        showChanges={showChanges}
        setShowChanges={setShowChanges}
        isEditingJson={isEditingJson}
        onToggleEditMode={handleToggleEditMode}
        onCancelEdit={() => setIsEditingJson(false)}
        isDownloading={isDownloading}
        onDownloadPDF={handleDownloadPDF}
        onSaveToExtension={handleSaveToExtension}
        onRetry={props.onRetry}
      />
      
      <div className="flex flex-1 flex-col md:flex-row bg-transparent relative z-10 w-full h-full min-h-0">
        <ResumeContent 
          containerRef={containerRef}
          containerWidth={containerWidth}
          isEditingJson={isEditingJson}
          editingData={editingData}
          setEditingData={setEditingData}
          isGenerating={isGenerating}
          pdfBlob={pdfBlob}
          zoom={zoom}
          setZoom={setZoom}
          setNumPages={setNumPages}
          numPages={numPages}
          resumeData={resumeData}
        />

        <ResumeFeedbackPanel
          json={json}
          history={history}
          historyIndex={historyIndex}
          handleUndo={handleUndo}
          handleRedo={handleRedo}
          feedback={feedback}
          setFeedback={setFeedback}
          handleGenerate={handleGenerate}
          handleCancel={handleCancelGenerate}
          isGenerating={isGenerating}
          resumeData={resumeData}
          error={error}
        />
      </div>
    </div>
  );
}
